import moment from 'moment';
import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import { ServiceType, Status } from '../../../enums/contractor';
import { sum } from '../../../../shared/utils';
import { BookingSetMenu } from '../../../types/set-menu';

export const OPTIONAL_SERVICE_ESTIMATE_SOURCE =
    'optionalServiceEstimate' as const;

export function getJobsGrandTotal(jobs: (Job | BookingSetMenu)[]): number {
    return sum(
        jobs.map((job) => {
            return job.providerType === ProviderType.Contractor
                ? sum(
                      (job.items ?? []).map(
                          (item) => (item.cost ?? 0) * (item.quantity ?? 0),
                      ),
                  )
                : (job.quantity ?? 0) * (job.pricePerPerson ?? 0);
        }),
    );
}

export function flattenReservationJobsFromForm(jobs: unknown): any[] {
    if (!jobs || typeof jobs !== 'object') return [];
    return Object.values(jobs as Record<string, unknown>).flatMap(
        (group: any) =>
            group && typeof group === 'object' ? Object.values(group) : [],
    );
}
export function jobHasSelectedContractor(job: unknown): boolean {
    if (!job || typeof job !== 'object') return false;
    const id = (job as { contractor?: { id?: unknown } }).contractor?.id;
    if (typeof id === 'number') return Number.isFinite(id) && id > 0;
    if (typeof id === 'string') return id.trim().length > 0;
    return false;
}

/** First non-empty message from a nested react-hook-form / Zod error tree. */
export function firstReservationFormErrorMessage(
    errors: Record<string, unknown> | undefined,
): string | undefined {
    if (!errors || typeof errors !== 'object') return undefined;
    for (const value of Object.values(errors)) {
        if (!value || typeof value !== 'object') continue;
        const maybe = value as { message?: unknown };
        if (typeof maybe.message === 'string' && maybe.message.length > 0) {
            return maybe.message;
        }
        const nested = firstReservationFormErrorMessage(
            value as Record<string, unknown>,
        );
        if (nested) return nested;
    }
    return undefined;
}

export function getOptionalServiceTotalsFromFormJobs(formJobs: unknown): {
    cateringExGst: number;
    alcoholExGst: number;
    massageExGst: number;
} {
    if (!formJobs || typeof formJobs !== 'object') {
        return { cateringExGst: 0, alcoholExGst: 0, massageExGst: 0 };
    }
    const j = formJobs as {
        catering?: Record<string, any>;
        alcohol?: Record<string, any>;
        wellness?: Record<string, any>;
    };
    const active = (rec?: Record<string, any>) =>
        Object.values(rec ?? {}).filter(
            (job: any) => job?.status !== Status.Cancelled,
        );
    return {
        cateringExGst: getJobsGrandTotal(active(j.catering)),
        alcoholExGst: getJobsGrandTotal(active(j.alcohol)),
        massageExGst: getJobsGrandTotal(active(j.wellness)),
    };
}

/** Synthetic expense rows for the edit-reservation charges sidebar (amounts ex GST, same as job cards). */
export function buildOptionalServiceExpenseLines(formJobs: unknown): any[] {
    const { cateringExGst, massageExGst, alcoholExGst } =
        getOptionalServiceTotalsFromFormJobs(formJobs);
    const lines: any[] = [];
    if (cateringExGst > 0) {
        lines.push({
            id: 'edit-optional-catering',
            name: 'Catering requests',
            amount: cateringExGst,
            description: 'Estimated total from current requests (ex GST)',
            source: OPTIONAL_SERVICE_ESTIMATE_SOURCE,
        });
    }
    if (alcoholExGst > 0) {
        lines.push({
            id: 'edit-optional-alcohol',
            name: 'Alcohol requests',
            amount: alcoholExGst,
            description: 'Estimated total from current requests (ex GST)',
            source: OPTIONAL_SERVICE_ESTIMATE_SOURCE,
        });
    }
    if (massageExGst > 0) {
        lines.push({
            id: 'edit-optional-massage',
            name: 'Wellness & lifestyle requests',
            amount: massageExGst,
            description: 'Estimated total from current requests (ex GST)',
            source: OPTIONAL_SERVICE_ESTIMATE_SOURCE,
        });
    }
    return lines;
}

/**
 * Flatten react-hook-form FieldErrors (incl. nested arrays like contractorPolicies) into user-facing strings.
 */
export function collectRhfErrorMessages(errors: unknown): string[] {
    const seen = new Set<string>();

    const walk = (node: unknown): void => {
        if (node === undefined || node === null) return;
        if (typeof node !== 'object') return;

        const obj = node as Record<string, unknown>;

        if (typeof obj.message === 'string') {
            const m = obj.message.trim();
            if (m) seen.add(m);
            return;
        }

        for (const [key, val] of Object.entries(obj)) {
            if (key === 'ref') continue;
            if (val === undefined || val === null) continue;
            if (Array.isArray(val)) {
                val.forEach(walk);
            } else if (typeof val === 'object') {
                walk(val);
            }
        }
    };

    walk(errors);
    return Array.from(seen);
}

/** Approved reservation whose date range includes today (local calendar days). */
export function isApprovedReservationCurrentlyActive(
    status: string | undefined,
    startDate: unknown,
    endDate: unknown,
): boolean {
    if (status !== 'approved') return false;
    if (startDate == null || endDate == null) return false;
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const today = moment().startOf('day');
    return today.isBetween(start, end, 'day', '[]');
}

export function formatYMD(date: any) {
    return moment(date).format('YYYY-MM-DD');
}

export function formatDMY(date: any) {
    return moment(date).format('DD/MM/YYYY');
}

export function addHoursHHmm(start: string, end: string) {
    const mins =
        moment.duration(start).asMinutes() + moment.duration(end).asMinutes();
    const h = ((mins / 60) | 0) % 24;
    const m = mins % 60;
    const z2 = (n: number) => (n < 10 ? '0' : '') + n;
    return z2(h) + ':' + z2(m);
}

export function subHoursHHmm(start: string, end: string) {
    const mins =
        moment.duration(start).asMinutes() - moment.duration(end).asMinutes();
    const h = ((mins / 60) | 0) % 24;
    const m = mins % 60;
    const z2 = (n: number) => (n < 10 ? '0' : '') + n;
    return z2(h) + ':' + z2(m);
}

export function positiveGuestCap(value: unknown): number | null {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(Math.floor(n), 10_000);
}

/** Max seating from selected spaces (commercial boardrooms carry `seatingCapacity`). */
export function maxSeatingFromCommercialSpaces(spaces: unknown): number {
    if (!Array.isArray(spaces)) return 0;
    let max = 0;
    for (const entry of spaces) {
        if (!entry || typeof entry !== 'object') continue;
        const s = entry as Record<string, unknown>;
        const direct = Number(s.seatingCapacity);
        if (Number.isFinite(direct) && direct > max) max = direct;
        const nested = s.space;
        if (nested && typeof nested === 'object') {
            const c = Number(
                (nested as Record<string, unknown>).seatingCapacity,
            );
            if (Number.isFinite(c) && c > max) max = c;
        }
    }
    return max;
}

export function buildReservationDateOptions(startDate: any, endDate: any) {
    const dateArray: string[] = [];
    if (!startDate || !endDate) return dateArray;

    const start = moment(startDate, 'YYYY-MM-DD');
    const end = moment(endDate, 'YYYY-MM-DD');

    for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
        dateArray.push(d.format('YYYY-MM-DD'));
    }

    return dateArray;
}

/**
 * When jobs are re-synced from the API into react-hook-form, preserve any
 * in-progress optional-service rows the user added locally (`newJob`).
 * Otherwise a refetch replaces `jobs.catering` / `jobs.wellness` and wipes them before save.
 */
export function mergeJobsFormWithServer(
    serverByKey: Record<string, any>,
    currentByKey: Record<string, any> | undefined,
): Record<string, any> {
    const next = { ...serverByKey };
    if (!currentByKey) return next;
    for (const [key, job] of Object.entries(currentByKey)) {
        if (job?.newJob) {
            next[key] = job;
        }
    }
    return next;
}

export function getReservationDateDisplay(
    reservationDetails: any,
    readableStartDate: string,
    readableEndDate: string,
    singleDate: string,
    getExperienceOwnedByLoggedInUser: () => boolean | undefined,
) {
    const type = reservationDetails?.module?.type;
    const isRange =
        type === 'property' ||
        type === 'vehicle' ||
        type === 'boat' ||
        type === 'activity' ||
        type === 'commercial' ||
        getExperienceOwnedByLoggedInUser() === true;

    return isRange ? `${readableStartDate} - ${readableEndDate}` : singleDate;
}

export function canEditReservationFields(
    reservationDetails: any,
    loggedInUser: any,
) {
    return !(
        reservationDetails?.status === 'approved' &&
        !loggedInUser?.role.includes('owner')
    );
}

export function currency(amount?: number, currencyCode: string = 'AUD') {
    if (amount === null || amount === undefined || Number.isNaN(amount))
        return '-';

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function pickTotalsFromFees(fees: any) {
    const charges = fees?.charges ?? [];

    const overallFeeTotal = (fees?.charges ?? []).reduce(
        (sum: number, c: any) => sum + (Number(c?.total) || 0),
        0,
    );

    const serviceFee =
        fees?.serviceFee ??
        charges
            .filter((c: any) =>
                String(c?.chargeType).toLowerCase().includes('service'),
            )
            .reduce(
                (acc: number, c: any) =>
                    acc + (Number(c?.total ?? c?.amount ?? 0) || 0),
                0,
            );

    const gst =
        fees?.gst ??
        charges
            .filter((c: any) =>
                String(c?.chargeType).toLowerCase().includes('gst'),
            )
            .reduce(
                (acc: number, c: any) =>
                    acc + (Number(c?.total ?? c?.amount ?? 0) || 0),
                0,
            );

    const total =
        fees?.bookingTotal?.total ??
        fees?.bookingTotal ??
        fees?.total ??
        overallFeeTotal + serviceFee + gst;

    return { overallFeeTotal, serviceFee, gst, total };
}

const CANCELLABLE_JOB_STATUSES = new Set<string>([
    Status.Pending,
    Status.PendingBooking,
    Status.Accepted,
    Status.Active,
    Status.PendingCompletion,
]);

function numericJobId(id: unknown): number | null {
    if (typeof id === 'number' && Number.isFinite(id)) return id;
    if (typeof id === 'string' && /^\d+$/.test(id)) return Number(id);
    return null;
}

/**
 * Jobs to cancel after a reservation edit.
 * Jobs that are still on the form stay, including jobs created outside the
 * "additional services" checkbox (recurring, itinerary, owner).
 * Turning that checkbox off in this edit cancels every other open job.
 * Cleaning jobs are never cancelled from a reservation edit.
 */
export function contractorJobIdsToCancel({
    existingJobs,
    jobsStillOnForm,
    turnedOffAdditionalServices,
}: {
    existingJobs: { id?: unknown; status?: string; serviceType?: string }[];
    jobsStillOnForm: { id?: unknown }[];
    turnedOffAdditionalServices: boolean;
}): number[] {
    const stillOnForm = new Set(
        jobsStillOnForm
            .map((job) => numericJobId(job.id))
            .filter((id): id is number => id != null),
    );

    return existingJobs
        .filter((job) => {
            const id = numericJobId(job.id);
            if (id == null) return false;
            if (job.serviceType === ServiceType.Cleaning) return false;
            if (!job.status || !CANCELLABLE_JOB_STATUSES.has(job.status)) {
                return false;
            }
            if (turnedOffAdditionalServices) return true;
            return !stillOnForm.has(id);
        })
        .map((job) => numericJobId(job.id) as number);
}

/**
 * Set menus to send with a reservation update. Menus still on the form are kept.
 * `requiresCatering` / `requiresBeverages` are notes flags and do not drop menus.
 * Turning off additional services drops every menu so the API cancels them.
 */
export function setMenusToSendOnReservationUpdate<T>(
    setMenusOnForm: T[],
    turnedOffAdditionalServices: boolean,
): T[] {
    if (turnedOffAdditionalServices) return [];
    return setMenusOnForm;
}
